import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { enqueueSnackbar } from '~/utils/notifications';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useCommitImportToRepository } from '~/hooks/domain/useCommitImport';
import { registerImportResult } from '~/hooks/useProcessDicomFiles';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { claimIngest, releaseIngest } from '~/hooks/dicomIngestOwnership';
import { useRefuseWhileViewerOpening } from '~/hooks/useRefuseWhileViewerOpening';
import {
    MATRIX_SIZE_CHOICES,
    useReformatParameters,
    useReformatSource,
} from '~/hooks/image/useReformatParameters';
import useLoadedSeriesNumbers from '~/hooks/image/useLoadedSeriesNumbers';
import {
    DomainError,
    DomainValidationError,
    ReformatUnsupportedError,
} from '~/domain/errors/DomainError';
import { formatDicomDateTime } from '~/domain/dicom/utils';
import { DicomVRMaxLength } from '~/domain/dicom/dicomConstants';
import {
    DicomStringViolation,
    INTEGER_STRING_MAX,
    dicomStringViolation,
} from '~/domain/dicom/valueRepresentation';
import { ImportResult } from '~/domain/services/dicomProcessor';
import {
    renderScoutProjection,
    ProjectionResult,
} from '~/domain/services/orthographicProjection';
import { cmToMm, formatMmAsCm, mmToCm } from '~/utils/units';
import InfoHint from '~/components/Inputs/InfoHint';
import useReformatSourceVolume from '~/hooks/image/useReformatSourceVolume';
import {
    MovedEdge,
    mirrorAboutCentre,
    realisedZRangeMm,
} from '~/domain/services/imageService';
import NumberInput from '~/components/Inputs/NumberInput';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import {
    executeReformat,
    MIN_THICKNESS_MM,
    MAX_THICKNESS_MM,
    MIN_FOV_MM,
    MAX_FOV_MM,
    MAX_EXTENSION_MARGIN_MM,
    MIN_EXTENSION_MARGIN_MM,
} from '~/domain/services/imageService';
import type {
    ReformatBlockedReason,
    ReformatTarget,
    ScoutAxis,
} from '~/domain/services/imageService';
import { REFORMAT_BLOCKED_MESSAGE_IDS } from './DatasetList/reformatBlockedMessages';
import OutputGeometry from './OutputGeometry';
import SourceGeometry from './SourceGeometry';
import ReformatWarnings from './ReformatWarnings';
import ProjectionCanvas from './ProjectionCanvas';
import ReformatPreview from './ReformatPreview';

/**
 * Width of the output-parameter column, px.
 *
 * The scouts get what is left, which must hold two of them side by side.
 * At the lg breakpoint the dialog paper is the viewport less 64, so
 * 1200 - 64 - 48 of padding - this - 16 of gap leaves 772 for a pair of
 * 320 px scouts.
 */
const PARAMETER_COLUMN_PX = 300;

/** What each way of breaking VR LO is called on the field. */
const DESCRIPTION_VIOLATION_MESSAGE_IDS: Record<DicomStringViolation, string> =
    {
        empty: 'seriesDescriptionTooLong',
        characters: 'seriesDescriptionInvalidChars',
        backslash: 'seriesDescriptionNoBackslash',
        length: 'seriesDescriptionTooLong',
    };

/** Side of each scout, px. Two are shown side by side (SR-WOR-H-34-2). */
const PROJECTION_MAX_SIZE = 320;

/** Side of the output preview, px. */
const PREVIEW_SIZE = 260;

/**
 * Field-of-view step, mm. Shared by the numeric input and the wheel over
 * the preview so the two land on the same values.
 */
const FOV_STEP_MM = 50;

/** The two projections, in the order they are shown. */
const SCOUTS: { axis: ScoutAxis; labelId: string }[] = [
    { axis: 'coronal', labelId: 'reformatCoronal' },
    { axis: 'sagittal', labelId: 'reformatSagittal' },
];

interface ReformatDialogProps {
    open: boolean;
    onClose: () => void;
    target: ReformatTarget;
    onReformatComplete: (importResult: ImportResult) => void;
}

export default function ReformatDialog({
    open,
    onClose,
    target,
    onReformatComplete,
}: ReformatDialogProps) {
    const intl = useIntl();
    const t = useCallback(
        (id: string, values?: Record<string, string | number>) =>
            intl.formatMessage({ id }, values),
        [intl]
    );
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const commitImportToRepository = useCommitImportToRepository();
    const { showBusy, hideBusy } = useBusyIndicator();
    const refuseWhileViewerOpening = useRefuseWhileViewerOpening();

    const source = useReformatSource(target);
    const usedSeriesNumbers = useLoadedSeriesNumbers();
    const {
        region,
        matrixSize,
        sliceThicknessMm,
        outputDescription,
        seriesNumber,
        seriesNumberTaken,
        setSeriesNumber,
        outputGrid,
        paddedFraction,
        paddedFractionStale,
        startBlockedReason,
        extension,
        replicatedSlices,
        extensionMarginMm,
        excludedMm,
        setExtensionEnabled,
        setExtensionMarginMm,
        setExtendSuperior,
        setExtendInferior,
        setFovMm,
        setInPlaneBounds,
        moveRegionBy,
        setZRange,
        setMatrixSize,
        setSliceThicknessMm,
        setOutputDescription,
    } = useReformatParameters(source, open, usedSeriesNumbers);

    const [isProcessing, setIsProcessing] = useState(false);
    /**
     * Whether an edge drag keeps the region centred where it is.
     *
     * On by default, because it is the less disruptive of the two: moving
     * one limit does not shift the anatomy the region is framed on. Turning
     * it off gives each edge its own position, which is what an off-centre
     * target needs.
     */

    // The source voxels the preview resamples, read once per target.
    const { volume: sourceVolume, failed: sourceVolumeFailed } =
        useReformatSourceVolume(target, open);

    /**
     * Slice the preview shows, patient mm, or null to follow the middle of
     * the region.
     *
     * Null rather than a number seeded from the region, because the region
     * moves under it: a position the user has not chosen should stay in the
     * middle of whatever they are currently framing, and one they HAVE
     * chosen should stay where they put it.
     */
    const [previewZMm, setPreviewZMm] = useState<number | null>(null);

    // Projections (compute both, display selected)
    const [coronalProj, setCoronalProj] = useState<ProjectionResult | null>(
        null
    );
    const [sagittalProj, setSagittalProj] = useState<ProjectionResult | null>(
        null
    );
    const [projectionsLoading, setProjectionsLoading] = useState(false);
    const [projectionFailed, setProjectionFailed] = useState(false);

    // Identity and acquisition details, shown only for a raw series: a
    // loaded image has them on its own properties panel already.
    const seriesInfo = useMemo(() => {
        if (target.kind !== 'rawSeries') {
            return null;
        }
        const slice = target.series.dicomImageSlices[0];
        return {
            seriesUid: target.series.seriesInstanceUID,
            forUid: target.series.frameOfReferenceUid,
            physicalSize: `${formatMmAsCm(slice.cols * slice.pixelSpacing[0])} × ${formatMmAsCm(slice.rows * slice.pixelSpacing[1])} cm`,
            scanDateTime: formatDicomDateTime(slice.resolvedScanDateTime),
        };
    }, [target]);

    useEffect(() => {
        if (open) {
            setIsProcessing(false);
            setPreviewZMm(null);
        }
    }, [open]);

    // Compute projections when dialog opens
    useEffect(() => {
        if (!open || source.slices.length === 0) {
            return;
        }

        setProjectionsLoading(true);
        setProjectionFailed(false);
        setCoronalProj(null);
        setSagittalProj(null);

        const timer = setTimeout(() => {
            try {
                setCoronalProj(
                    renderScoutProjection(source.slices, 'coronal', 512)
                );
                setSagittalProj(
                    renderScoutProjection(source.slices, 'sagittal', 512)
                );
            } catch (e) {
                // A blank scout with the controls still live would let the
                // user frame the output against nothing.
                console.error(
                    'Error rendering projections:',
                    e instanceof Error ? e.name : typeof e
                );
                setProjectionFailed(true);
            } finally {
                setProjectionsLoading(false);
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [open, source.slices]);

    const handleZRangeChange = useCallback(
        (_: Event, newValue: number | number[]) => {
            if (Array.isArray(newValue)) {
                setZRange([newValue[0], newValue[1]]);
            }
        },
        [setZRange]
    );

    const handleFovChange = useCallback(
        (_: Event, newValue: number | number[]) => {
            if (typeof newValue === 'number') {
                setFovMm(newValue);
            }
        },
        [setFovMm]
    );

    // Canvas drag callbacks
    const handleCanvasZRangeChange = useCallback(
        (newRange: [number, number], moved: MovedEdge) => {
            const clamped: [number, number] = [
                Math.max(source.region.zRangeMm[0], newRange[0]),
                Math.min(source.region.zRangeMm[1], newRange[1]),
            ];
            setZRange(
                mirrorAboutCentre(
                    region.zRangeMm,
                    clamped,
                    moved,
                    source.region.zRangeMm
                )
            );
        },
        [setZRange, source.region.zRangeMm, region.zRangeMm]
    );

    const handleCanvasInPlaneBounds = useCallback(
        (axis: ScoutAxis, bounds: [number, number], moved: MovedEdge) => {
            const inPlane = axis === 'coronal' ? 'x' : 'y';
            const centre =
                inPlane === 'x' ? region.centerXMm : region.centerYMm;
            // The field of view is one width applied to both in-plane axes,
            // so the current pair is always centred on this axis's centre.
            const current: [number, number] = [
                centre - region.fovMm / 2,
                centre + region.fovMm / 2,
            ];
            setInPlaneBounds(
                inPlane,
                mirrorAboutCentre(current, bounds, moved)
            );
        },
        [setInPlaneBounds, region]
    );

    const handleCanvasMove = useCallback(
        (axis: ScoutAxis, inPlaneDeltaMm: number, zDeltaMm: number) => {
            moveRegionBy(
                axis === 'coronal'
                    ? { xMm: inPlaneDeltaMm, zMm: zDeltaMm }
                    : { yMm: inPlaneDeltaMm, zMm: zDeltaMm }
            );
        },
        [moveRegionBy]
    );

    // SeriesDescription is VR LO and Type 3, so an empty value is legal.
    const descriptionViolation = dicomStringViolation(
        outputDescription,
        DicomVRMaxLength.LONG,
        { allowEmpty: true }
    );

    const extensionMm = useMemo(
        () => ({
            inferior: extension.inferior ? extensionMarginMm : 0,
            superior: extension.superior ? extensionMarginMm : 0,
        }),
        [extension, extensionMarginMm]
    );

    // The extension is part of the output, so the preview reaches over it
    // too — that is where the replicated slices are, and seeing one is the
    // point of being able to look.
    const realisedZRange = useMemo(
        () => realisedZRangeMm(region, sliceThicknessMm),
        [region, sliceThicknessMm]
    );

    const previewRange = useMemo<[number, number]>(
        () => [
            realisedZRange[0] - extensionMm.inferior,
            realisedZRange[1] + extensionMm.superior,
        ],
        [realisedZRange, extensionMm]
    );

    // Snapped to an output slice centre, so what is shown is a slice the
    // output will contain rather than a plane between two of them.
    const shownZMm = useMemo(() => {
        const requested =
            previewZMm ?? (realisedZRange[0] + realisedZRange[1]) / 2;
        const clamped = Math.min(
            previewRange[1],
            Math.max(previewRange[0], requested)
        );
        const steps = Math.round(
            (clamped - realisedZRange[0]) / sliceThicknessMm
        );
        return realisedZRange[0] + steps * sliceThicknessMm;
    }, [previewZMm, previewRange, realisedZRange, sliceThicknessMm]);

    /**
     * Panning the output preview.
     *
     * The preview shows the output and nothing else, so its frame IS the
     * field of view: dragging the picture moves the anatomy under a fixed
     * frame, which means the region travels the other way. That is the
     * opposite convention from the scouts, where the frame is a box drawn
     * on a larger picture and dragging it moves the box with the cursor —
     * and it is the one every image viewer uses for a pan.
     */
    const handlePreviewPan = useCallback(
        (dxMm: number, dyMm: number) => {
            moveRegionBy({ xMm: -dxMm, yMm: -dyMm });
        },
        [moveRegionBy]
    );

    /**
     * Wheel over the preview.
     *
     * Clamped here because `setFovMm` REFUSES a value outside the allowed
     * range rather than clamping it — which is right for a typed value,
     * where silently changing what someone entered is worse than declining
     * it, and wrong for a wheel: the field of view is rarely on the step
     * lattice, so the last notch before a limit fell outside the range and
     * was rejected whole, leaving the wheel inert short of a limit the
     * slider could still reach.
     */
    const handlePreviewZoom = useCallback(
        (direction: number) => {
            setFovMm(
                Math.min(
                    MAX_FOV_MM,
                    Math.max(MIN_FOV_MM, region.fovMm + direction * FOV_STEP_MM)
                )
            );
        },
        [setFovMm, region.fovMm]
    );

    const handleReformat = useCallback(async () => {
        // The reformat parses and commits into the shared DicomProcessor
        // without passing through processFiles, so it holds the same
        // exclusive-feed guard, before it parses anything.
        if (refuseWhileViewerOpening()) {
            return;
        }
        // And the same single-owner ingest token processFiles takes.
        if (!claimIngest()) {
            enqueueSnackbar(t('reformatIngestBusy'), { variant: 'warning' });
            return;
        }
        setIsProcessing(true);
        await showBusy(t('reformatting'));
        try {
            const result = await executeReformat({
                target,
                region,
                extension,
                sliceThicknessMm,
                resampleSize: matrixSize,
                outputDescription,
                seriesNumber,
                usedSeriesNumbers,
            });

            // Process through DicomProcessor (infrastructure boundary).
            // A slice that fails to parse comes back null, and dropping it
            // silently would commit the rest as the output series — a
            // volume short some slices, with nothing to say which. The
            // whole reformat fails instead.
            for (const ds of result.dicomDatasets) {
                if (dicomProcessor.dicomToImageSlice(ds) === null) {
                    throw new DomainValidationError(t('reformatError'));
                }
            }
            // toRepository calls patient.addImage, which fires markChanged
            // when a tracker is attached — so it must run inside a batch or
            // it throws, leaving the image in the aggregate but invisible to
            // the version-driven views. Same wrapper as useProcessDicomFiles.
            const processedItems = commitImportToRepository(() =>
                dicomProcessor.toRepository(patientRepository)
            );

            if (processedItems) {
                // Published before the output becomes the primary image,
                // so the grid's primary row can bind it.
                registerImportResult(
                    getDatasetEnvironmentRegistry(),
                    patientRepository.getAll(),
                    processedItems
                );
                onReformatComplete(processedItems);
            }

            onClose();
        } catch (e) {
            // A refusal carries its condition as a code, not as prose: it
            // is shown in the same words the start control uses. Other
            // domain errors carry a written message, as the ROI,
            // structure-set and registration command hooks assume.
            if (e instanceof ReformatUnsupportedError) {
                const id =
                    REFORMAT_BLOCKED_MESSAGE_IDS[
                        e.reason as ReformatBlockedReason
                    ];
                enqueueSnackbar(id ? t(id) : t('reformatError'), {
                    variant: 'warning',
                });
            } else if (e instanceof DomainError) {
                enqueueSnackbar(e.message, { variant: 'warning' });
            } else {
                // Only the error's TYPE. Errors raised below this dialog
                // interpolate Series and SOP Instance UIDs into their
                // messages, and a UID in a console is PHI in a console.
                console.error(
                    'Error during reformatting:',
                    e instanceof Error ? e.name : typeof e
                );
                enqueueSnackbar(t('reformatError'), { variant: 'error' });
            }
        } finally {
            releaseIngest();
            hideBusy();
            setIsProcessing(false);
        }
    }, [
        target,
        region,
        extension,
        sliceThicknessMm,
        matrixSize,
        outputDescription,
        seriesNumber,
        usedSeriesNumbers,
        dicomProcessor,
        patientRepository,
        commitImportToRepository,
        onReformatComplete,
        onClose,
        showBusy,
        hideBusy,
        t,
        refuseWhileViewerOpening,
    ]);

    const { geometry } = source;

    return (
        <Dialog
            data-testid="reformat-dialog"
            open={open}
            onClose={isProcessing ? undefined : onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ elevation: 0 }}
        >
            <DialogTitle>
                {intl.formatMessage(
                    { id: 'reformatImageTitle' },
                    {
                        modality: source.modality,
                        slices: source.slices.length,
                        matrix: `${geometry.cols}×${geometry.rows}`,
                        description: source.seriesDescription,
                    }
                )}
            </DialogTitle>
            <DialogContent sx={{ overflowX: 'hidden' }}>
                {/* ── Scout + Output parameters side by side ── */}
                {/*
                    A grid rather than a flex row: `flex={0}` on the
                    parameter column expands to `flex: 0 1 0%`, which
                    overrode its `flexBasis` and let it size to its own
                    content — around 660 px — leaving the scouts less than
                    the width of two, so they wrapped and the dialog grew a
                    scout taller than the screen. The track is stated once
                    here and cannot be argued with by a shorthand.
                */}
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns={{
                        xs: '1fr',
                        lg: `1fr ${PARAMETER_COLUMN_PX}px`,
                    }}
                >
                    {/* Left: scout views */}
                    <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                            {t('scoutView')}
                        </Typography>

                        {projectionsLoading ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                                py={8}
                            >
                                <CircularProgress size={24} />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('reformatScoutsLoading')}
                                </Typography>
                            </Box>
                        ) : projectionFailed ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                py={8}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('reformatScoutsFailed')}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Both scouts at once: the region is one
                                    box in three dimensions, and framing it
                                    on one projection at a time hid what the
                                    same drag did to the other. */}
                                {/*
                                    Side by side or not at all. Wrapping
                                    made the pair stack, which is a scout
                                    taller and is why the dialog outgrew
                                    the screen; below the breakpoint the
                                    whole layout stacks instead, and there
                                    the scouts share a scrolling row.
                                */}
                                <Box
                                    display="flex"
                                    gap={1}
                                    flexWrap="nowrap"
                                    sx={{
                                        overflowX: 'auto',
                                        // `safe center` rather than
                                        // `center`: centred content that
                                        // overflows spills to BOTH sides
                                        // and only the end side can be
                                        // scrolled to, so a narrow window
                                        // put the first scout's drag
                                        // handles somewhere unreachable.
                                        justifyContent: 'safe center',
                                    }}
                                >
                                    {SCOUTS.map(({ axis, labelId }) => (
                                        <ProjectionCanvas
                                            key={axis}
                                            projection={
                                                axis === 'coronal'
                                                    ? coronalProj
                                                    : sagittalProj
                                            }
                                            label={t(labelId)}
                                            axis={axis}
                                            zRange={realisedZRange}
                                            fovMm={region.fovMm}
                                            inPlaneCenterMm={
                                                axis === 'coronal'
                                                    ? region.centerXMm
                                                    : region.centerYMm
                                            }
                                            maxSize={PROJECTION_MAX_SIZE}
                                            previewZMm={shownZMm}
                                            onZRangeChange={
                                                handleCanvasZRangeChange
                                            }
                                            onInPlaneBoundsChange={(
                                                bounds,
                                                moved
                                            ) =>
                                                handleCanvasInPlaneBounds(
                                                    axis,
                                                    bounds,
                                                    moved
                                                )
                                            }
                                            onRegionMove={(
                                                inPlaneDeltaMm,
                                                zDeltaMm
                                            ) =>
                                                handleCanvasMove(
                                                    axis,
                                                    inPlaneDeltaMm,
                                                    zDeltaMm
                                                )
                                            }
                                            extensionMm={extensionMm}
                                        />
                                    ))}
                                </Box>

                                <Divider sx={{ my: 1 }} />

                                {/* The output itself, one slice of it. The
                                    scouts show where the region sits on the
                                    source; this shows what comes out. */}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={2}
                                >
                                    <ReformatPreview
                                        source={sourceVolume}
                                        failed={sourceVolumeFailed}
                                        region={region}
                                        matrixSize={matrixSize}
                                        sliceThicknessMm={sliceThicknessMm}
                                        extension={extension}
                                        realisedZRange={realisedZRange}
                                        zMm={shownZMm}
                                        sizePx={PREVIEW_SIZE}
                                        // The two gestures are
                                        // orthogonal: the wheel resizes
                                        // about the centre, the drag moves
                                        // the centre.
                                        onPan={handlePreviewPan}
                                        onZoom={handlePreviewZoom}
                                    />
                                    <Box
                                        height={PREVIEW_SIZE}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t('reformatOutputPreview')}
                                        </Typography>
                                        <Slider
                                            orientation="vertical"
                                            size="small"
                                            value={shownZMm}
                                            min={previewRange[0]}
                                            max={previewRange[1]}
                                            step={sliceThicknessMm}
                                            onChange={(_, v) =>
                                                typeof v === 'number' &&
                                                setPreviewZMm(v)
                                            }
                                            disabled={
                                                isProcessing || !sourceVolume
                                            }
                                            sx={{ my: 1 }}
                                        />
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Right: output parameters */}
                    <Box
                        minWidth={0}
                        display="flex"
                        flexDirection="column"
                        gap={1.5}
                    >
                        <Typography variant="subtitle2">
                            {t('reformatOutputImage')}
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            label={t('seriesDescriptionLabel')}
                            // The length is capped as it is typed; the
                            // character rules cannot be, so they are
                            // reported. Both are the same predicate the
                            // reformat refuses on.
                            inputProps={{
                                maxLength: DicomVRMaxLength.LONG,
                            }}
                            value={outputDescription}
                            onChange={(e) =>
                                setOutputDescription(e.target.value)
                            }
                            error={descriptionViolation !== null}
                            helperText={
                                descriptionViolation === null
                                    ? undefined
                                    : t(
                                          DESCRIPTION_VIOLATION_MESSAGE_IDS[
                                              descriptionViolation
                                          ]
                                      )
                            }
                            disabled={isProcessing}
                        />

                        <NumberInput
                            fullWidth
                            label={t('seriesNumberLabel')}
                            value={seriesNumber}
                            onChange={(v) => {
                                if (v !== null) {
                                    setSeriesNumber(v);
                                }
                            }}
                            min={1}
                            max={INTEGER_STRING_MAX}
                            step={1}
                            precision={0}
                            error={
                                seriesNumberTaken
                                    ? t('seriesNumberTaken')
                                    : null
                            }
                            disabled={isProcessing}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel>{t('reformatMatrix')}</InputLabel>
                            <Select
                                data-testid="reformat-output-size"
                                value={matrixSize}
                                label={t('reformatMatrix')}
                                onChange={(e) =>
                                    setMatrixSize(Number(e.target.value))
                                }
                                disabled={isProcessing}
                            >
                                {!MATRIX_SIZE_CHOICES.includes(
                                    source.matrixSize
                                ) && (
                                    <MenuItem value={source.matrixSize}>
                                        {source.matrixSize} ×{' '}
                                        {source.matrixSize} ({t('original')})
                                    </MenuItem>
                                )}
                                {MATRIX_SIZE_CHOICES.map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size} × {size}
                                        {source.matrixSize === size
                                            ? ` (${t('original')})`
                                            : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <NumberInput
                            fullWidth
                            label={t('sliceThicknessCm')}
                            value={mmToCm(sliceThicknessMm)}
                            onChange={(v) => {
                                if (v !== null) {
                                    setSliceThicknessMm(cmToMm(v));
                                }
                            }}
                            min={mmToCm(MIN_THICKNESS_MM)}
                            max={mmToCm(MAX_THICKNESS_MM)}
                            step={0.05}
                            snapToStep
                            stepOnWheel
                            precision={2}
                            disabled={isProcessing}
                        />

                        <NumberInput
                            fullWidth
                            // The square field of view is the thing users
                            // read the scouts wrongly about: a limit
                            // dragged on one scout changes the width on
                            // both, because there is one extent, not two.
                            label={
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    {t('fieldOfViewCm')}
                                    <InfoHint
                                        title={t('fieldOfViewInfo')}
                                        placement="top-start"
                                    />
                                </Box>
                            }
                            value={mmToCm(region.fovMm)}
                            onChange={(v) => {
                                if (v !== null) {
                                    setFovMm(cmToMm(v));
                                }
                            }}
                            min={mmToCm(MIN_FOV_MM)}
                            max={mmToCm(MAX_FOV_MM)}
                            step={mmToCm(FOV_STEP_MM)}
                            snapToStep
                            stepOnWheel
                            precision={1}
                            disabled={isProcessing}
                        />
                        <Slider
                            value={region.fovMm}
                            onChange={handleFovChange}
                            min={MIN_FOV_MM}
                            max={MAX_FOV_MM}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${formatMmAsCm(v, 1)} cm`}
                            disabled={isProcessing}
                            sx={(theme) => ({
                                mt: -1.5,
                                '& .MuiSlider-markLabel': {
                                    fontSize: '0.65rem',
                                    color: 'text.secondary',
                                },
                                '& .MuiSlider-track': {
                                    color: theme.palette.secondary.dark,
                                },
                                '& .MuiSlider-rail': {
                                    color: theme.palette.secondary.darker,
                                },
                                '& .MuiSlider-thumb': {
                                    color: theme.palette.secondary.dark,
                                    borderColor: theme.palette.secondary.dark,
                                },
                            })}
                        />

                        <Box>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="baseline"
                            >
                                <Typography variant="body2" gutterBottom>
                                    {t('sliceRange')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {formatMmAsCm(region.zRangeMm[0])}
                                    {' – '}
                                    {formatMmAsCm(region.zRangeMm[1])} cm
                                </Typography>
                            </Box>
                            <Slider
                                value={region.zRangeMm}
                                onChange={handleZRangeChange}
                                min={source.region.zRangeMm[0]}
                                max={source.region.zRangeMm[1]}
                                step={0.5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) =>
                                    `${formatMmAsCm(v)} cm`
                                }
                                disabled={isProcessing}
                                sx={{
                                    '& .MuiSlider-track': {
                                        color: 'rgba(0, 200, 255, 0.9)',
                                    },
                                    '& .MuiSlider-thumb': {
                                        color: 'rgba(0, 200, 255, 0.9)',
                                    },
                                }}
                            />
                        </Box>

                        <Divider />

                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        data-testid="reformat-extend"
                                        checked={
                                            extension.superior ||
                                            extension.inferior
                                        }
                                        indeterminate={
                                            extension.superior !==
                                            extension.inferior
                                        }
                                        onChange={(_, checked) =>
                                            setExtensionEnabled(checked)
                                        }
                                        disabled={isProcessing}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {t('reformatExtendForScatter')}
                                        <InfoHint
                                            title={t(
                                                'reformatExtendForScatterInfo'
                                            )}
                                            placement="top-start"
                                        />
                                    </Typography>
                                }
                            />
                            {(extension.superior || extension.inferior) && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                    pl={1}
                                    // An outlined input's label floats over
                                    // its top edge, so it needs room above
                                    // or it lands on the checkbox row.
                                    mt={1.5}
                                >
                                    <NumberInput
                                        fullWidth
                                        label={t('reformatExtensionMarginCm')}
                                        value={mmToCm(extension.marginMm)}
                                        onChange={(v) => {
                                            if (v !== null) {
                                                setExtensionMarginMm(cmToMm(v));
                                            }
                                        }}
                                        min={mmToCm(MIN_EXTENSION_MARGIN_MM)}
                                        max={mmToCm(MAX_EXTENSION_MARGIN_MM)}
                                        step={1}
                                        snapToStep
                                        stepOnWheel
                                        precision={1}
                                        disabled={isProcessing}
                                    />
                                    <Box display="flex" gap={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    data-testid="reformat-extend-superior"
                                                    checked={extension.superior}
                                                    onChange={(_, checked) =>
                                                        setExtendSuperior(
                                                            checked
                                                        )
                                                    }
                                                    disabled={isProcessing}
                                                />
                                            }
                                            label={
                                                <Typography variant="caption">
                                                    {t('reformatSuperior')}
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    data-testid="reformat-extend-inferior"
                                                    checked={extension.inferior}
                                                    onChange={(_, checked) =>
                                                        setExtendInferior(
                                                            checked
                                                        )
                                                    }
                                                    disabled={isProcessing}
                                                />
                                            }
                                            label={
                                                <Typography variant="caption">
                                                    {t('reformatInferior')}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Divider />

                        <OutputGeometry grid={outputGrid} />

                        <Divider />

                        <SourceGeometry
                            geometry={geometry}
                            seriesInfo={seriesInfo}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <ReformatWarnings
                    paddedFraction={paddedFraction}
                    replicatedSlices={replicatedSlices}
                    extensionMarginMm={extensionMarginMm}
                    excludedMm={excludedMm}
                    gapFilledMm={sourceVolume?.gapFilledMm ?? 0}
                />
                <Button
                    data-testid="reformat-cancel-button"
                    onClick={onClose}
                    disabled={isProcessing}
                >
                    <FormattedMessage id="cancelReformat" />
                </Button>
                <Tooltip
                    title={
                        startBlockedReason
                            ? t(
                                  REFORMAT_BLOCKED_MESSAGE_IDS[
                                      startBlockedReason
                                  ]
                              )
                            : projectionFailed
                              ? t('reformatScoutsFailed')
                              : paddedFractionStale
                                ? t('reformatMeasuringPadding')
                                : ''
                    }
                >
                    {/* A disabled MUI Button emits no pointer events, so the
                        tooltip that says WHY needs a wrapper that still
                        does. */}
                    <span>
                        <Button
                            data-testid="reformat-start-button"
                            variant="contained"
                            onClick={handleReformat}
                            disabled={
                                isProcessing ||
                                startBlockedReason !== null ||
                                seriesNumberTaken ||
                                descriptionViolation !== null ||
                                projectionFailed ||
                                // The padded proportion is stated beside
                                // this button and lags a drag by a render
                                // or so. Starting inside that window would
                                // record a proportion the operator never
                                // saw.
                                paddedFractionStale
                            }
                            startIcon={
                                isProcessing ? (
                                    <CircularProgress size={16} />
                                ) : undefined
                            }
                        >
                            {isProcessing ? (
                                <FormattedMessage id="reformatting" />
                            ) : (
                                <FormattedMessage id="startReformat" />
                            )}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
}
